/**
 * Pure logic behind the mail feed's live 30-day window (no React, no Firestore).
 *
 * Why this exists: the feed used to be ONE query (date >= 30d, newest 1000) over the whole
 * `emails` collection. The automated registrations@ mailbox alone took ~520 of those 1000
 * slots, so the real window for the owner's own mail shrank to ~23 days and kept shrinking.
 * The feed now runs one listener PER MAILBOX, each with its own limit, so a noisy mailbox can
 * only ever exhaust its own budget.
 *
 * This module holds everything that is decidable without I/O: per-mailbox limits, coverage
 * edges, snapshot merging, the "keep search-hydrated mail" rule, pagination cursor choice, and
 * a small coordinator that owns listener lifecycle (gating, diffing, legacy fallback) against
 * an injected `subscribe`, so the React effect stays thin and the lifecycle is unit-tested.
 */

export const FEED_WINDOW_DAYS = 30;

/** Used until system_config/mail_source delivers the real list (and whenever it has none). */
export const DEFAULT_FEED_MAILBOXES: ReadonlyArray<string> = [
  'ben@webconstruct.com.au',
  'lidia@webconstruct.com.au',
  'registrations@webconstruct.com.au',
];

/** Automated mailboxes that would otherwise crowd real mail out of a shared budget. */
export const NOISY_MAILBOXES: ReadonlyArray<string> = [
  'registrations@webconstruct.com.au',
  'leads@webconstruct.com.au',
];

export const MAILBOX_FEED_LIMIT = 800;
export const NOISY_MAILBOX_FEED_LIMIT = 250;
/** The original single-query limit; only used by the legacy fallback. */
export const LEGACY_FEED_LIMIT = 1000;

export type Unsubscribe = () => void;

// -- mailbox list ------------------------------------------------------------------------------

/** Lower-case + trim; '' for anything that is not an address (the server stores mailbox lower-cased). */
export function normalizeMailboxAddress(value: unknown): string {
  if (typeof value !== 'string') return '';
  const v = value.trim().toLowerCase();
  return v.indexOf('@') > 0 ? v : '';
}

/** Clean a raw `mailboxes` field: lower-cased, de-duplicated, first-seen order; [] when unusable. */
export function normalizeMailboxList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  raw.forEach((item) => {
    const addr = normalizeMailboxAddress(item);
    if (addr && !seen.has(addr)) {
      seen.add(addr);
      out.push(addr);
    }
  });
  return out;
}

/** The mailboxes the feed should listen to: the configured list, else the hard-coded default. */
export function resolveFeedMailboxes(list: ReadonlyArray<string> | null | undefined): string[] {
  const normalized = normalizeMailboxList(list);
  return normalized.length > 0 ? normalized : DEFAULT_FEED_MAILBOXES.slice();
}

export function sameMailboxSet(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  if (a.length !== b.length) return false;
  const inA = new Set<string>(a);
  return b.every((m) => inA.has(m));
}

/** What changed between the listeners we hold and the list we now want. Order-insensitive. */
export function diffMailboxes(
  current: ReadonlyArray<string>,
  next: ReadonlyArray<string>
): { added: string[]; removed: string[] } {
  const have = new Set<string>(current);
  const want = new Set<string>(next);
  return {
    added: next.filter((m, i) => !have.has(m) && next.indexOf(m) === i),
    removed: current.filter((m) => !want.has(m)),
  };
}

export function feedLimitForMailbox(mailbox: string): number {
  return NOISY_MAILBOXES.indexOf(normalizeMailboxAddress(mailbox)) >= 0
    ? NOISY_MAILBOX_FEED_LIMIT
    : MAILBOX_FEED_LIMIT;
}

/** Start of the live window: calendar-day arithmetic in local time, same as the original query. */
export function thirtyDayCutoff(now: Date): Date {
  const d = new Date(now.getTime());
  d.setDate(d.getDate() - FEED_WINDOW_DAYS);
  return d;
}

// -- windows -----------------------------------------------------------------------------------

/** The slice of an email the window logic needs (FeedEmailData satisfies it structurally). */
export interface WindowEmail {
  id: string;
  createdAt: Date;
  mailbox?: string;
}

/** What a listener hands the coordinator for one snapshot. */
export interface RawWindow<T extends WindowEmail> {
  /** Converted docs that survived the mail-source-mode filter, newest first. */
  emails: T[];
  /** Docs the QUERY returned, before the mode filter: this is what "hit its limit" is judged on. */
  rawCount: number;
  /** The oldest doc the query returned (may itself have been filtered out of `emails`). */
  oldestRaw: T | null;
}

/**
 * One listener's current view of the world.
 *
 * `edgeMs` is the coverage edge: everything at or after that instant is complete for this
 * listener, everything before it was not asked for (or was cut off by the limit). `cursor` is the
 * opaque Firestore value for that instant (null = use the 30-day cutoff).
 */
export interface FeedWindow<T extends WindowEmail, C = unknown> {
  /** Lower-case mailbox address; null for the legacy whole-collection query. */
  mailbox: string | null;
  emails: T[];
  edgeMs: number;
  cursor: C | null;
  hitLimit: boolean;
}

const msOf = (e: WindowEmail): number => {
  const v = e.createdAt && typeof e.createdAt.getTime === 'function' ? e.createdAt.getTime() : 0;
  return Number.isFinite(v) ? v : 0;
};

/**
 * Newest first. Ties break on id DESCENDING, which is Firestore's own order for `orderBy(date, desc)`
 * and, unlike arrival order, never depends on which listener answered last (no row jitter).
 */
export function newestFirst(a: WindowEmail, b: WindowEmail): number {
  const d = msOf(b) - msOf(a);
  if (d !== 0) return d;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/**
 * A per-mailbox window. The coverage edge is the oldest doc ONLY when the query hit its limit
 * (older docs were cut off); otherwise the query genuinely covered the whole 30 days so the edge
 * is the cutoff itself.
 */
export function mailboxWindow<T extends WindowEmail, C>(
  mailbox: string,
  raw: RawWindow<T>,
  limit: number,
  cutoffMs: number,
  cursorOf: (email: T) => C
): FeedWindow<T, C> {
  const oldestMs = raw.oldestRaw ? msOf(raw.oldestRaw) : 0;
  const hitLimit = raw.rawCount >= limit && raw.oldestRaw !== null && oldestMs > 0;
  if (!hitLimit) {
    return { mailbox, emails: raw.emails, edgeMs: cutoffMs, cursor: null, hitLimit: false };
  }
  return {
    mailbox,
    emails: raw.emails,
    edgeMs: Math.max(oldestMs, cutoffMs),
    cursor: cursorOf(raw.oldestRaw as T),
    hitLimit: true,
  };
}

/** The legacy single-query window: edge = oldest surviving email, exactly as the original code did. */
export function legacyWindow<T extends WindowEmail, C>(
  raw: RawWindow<T>,
  cutoffMs: number,
  cursorOf: (email: T) => C
): FeedWindow<T, C> {
  const last = raw.emails.length > 0 ? raw.emails[raw.emails.length - 1] : null;
  const lastMs = last ? msOf(last) : 0;
  const usable = last !== null && lastMs > 0;
  return {
    mailbox: null,
    emails: raw.emails,
    edgeMs: usable ? lastMs : cutoffMs,
    cursor: usable ? cursorOf(last as T) : null,
    hitLimit: raw.rawCount >= LEGACY_FEED_LIMIT,
  };
}

/** Union of every window's emails (deduped by id, newest first) and the id allowlist it implies. */
export function unionWindowEmails<T extends WindowEmail>(
  windows: ReadonlyArray<FeedWindow<T, any>>
): { emails: T[]; ids: Set<string> } {
  const ids = new Set<string>();
  const emails: T[] = [];
  windows.forEach((w) => {
    w.emails.forEach((e) => {
      if (!ids.has(e.id)) {
        ids.add(e.id);
        emails.push(e);
      }
    });
  });
  emails.sort(newestFirst);
  return { emails, ids };
}

/**
 * Fold the previous feed into a fresh union. The windows are authoritative only inside their
 * coverage, so a previous email (search hydration, paginated-in older mail) survives iff:
 *   - it is not re-delivered by a window (then the canonical doc replaces the local copy), AND
 *   - it is older than the coverage edge of its mailbox's window. Newer + absent means the doc
 *     was deleted or moved out of the window, so it is dropped.
 * Mail no live window covers (mailbox not listened to, legacy doc without a mailbox) can only have
 * arrived by hydration and is kept. `mailbox: null` (legacy) windows cover every mailbox.
 */
export function mergeWithExternal<T extends WindowEmail>(
  prev: ReadonlyArray<T>,
  union: { emails: T[]; ids: ReadonlySet<string> },
  windows: ReadonlyArray<FeedWindow<T, any>>
): T[] {
  let whole: FeedWindow<T, any> | null = null;
  const byMailbox = new Map<string, FeedWindow<T, any>>();
  for (let i = 0; i < windows.length; i++) {
    const w = windows[i];
    if (w.mailbox === null) whole = w;
    else byMailbox.set(w.mailbox, w);
  }
  const kept = prev.filter((e) => {
    if (union.ids.has(e.id)) return false;
    const dateMs = msOf(e);
    if (!(dateMs > 0)) return false;
    const cover = whole || byMailbox.get(normalizeMailboxAddress(e.mailbox));
    if (!cover) return true;
    return dateMs < cover.edgeMs;
  });
  if (kept.length === 0) return union.emails;
  return union.emails.concat(kept).sort(newestFirst);
}

/**
 * Where "load older" should continue from. Everything NEWER than the returned edge is complete
 * across every window, so the whole-collection `date < edge` query misses nothing. That is the
 * NEWEST coverage edge among the windows: a limit-hit window's edge, else the 30-day cutoff.
 * (Taking the oldest edge instead would skip, for a second limit-hit mailbox, the stretch
 * between the two edges.) `cursor` is the exact Firestore value, null = use the cutoff.
 */
export function paginationEdge<C>(
  windows: ReadonlyArray<FeedWindow<any, C>>,
  cutoffMs: number
): { ms: number; cursor: C | null } {
  let best: { ms: number; cursor: C | null } = { ms: cutoffMs, cursor: null };
  windows.forEach((w) => {
    if (w.edgeMs > best.ms) best = { ms: w.edgeMs, cursor: w.cursor };
  });
  return best;
}

/** Append a page of older mail without ever duplicating an email already in the feed. */
export function appendOlderEmails<T extends WindowEmail>(prev: T[], older: ReadonlyArray<T>): T[] {
  if (older.length === 0) return prev;
  const have = new Set<string>(prev.map((e) => e.id));
  const fresh = older.filter((e) => {
    if (have.has(e.id)) return false;
    have.add(e.id);
    return true;
  });
  if (fresh.length === 0) return prev;
  return prev.concat(fresh).sort(newestFirst);
}

// -- listener lifecycle ------------------------------------------------------------------------

export interface FeedListenerHandlers<T extends WindowEmail> {
  onSnapshot: (raw: RawWindow<T>) => void;
  onError: (err: unknown) => void;
}

export interface FeedCoordinatorHooks<T extends WindowEmail, C> {
  cutoffMs: number;
  /** The Firestore cursor value for an email (its `date` Timestamp). */
  cursorOf: (email: T) => C;
  /** Open ONE query listener. mailbox === null is the legacy whole-collection query. */
  subscribe: (mailbox: string | null, limit: number, handlers: FeedListenerHandlers<T>) => Unsubscribe;
  /**
   * The merged state changed. `firstOfSource` = this publish carries the first snapshot of a
   * source (initial load, a newly added mailbox, the legacy fallback): mail that was already
   * there, so the caller must not ring the new-mail sound for it.
   */
  publish: (windows: FeedWindow<T, C>[], firstOfSource: boolean) => void;
  /** Fires once: every listener answered (or the legacy fallback answered / failed). */
  initialLoaded: () => void;
  /** Per-mailbox listeners were abandoned for the legacy query. The caller logs the ONE warning. */
  fallback: (reason: unknown) => void;
  /** The legacy listener itself failed. */
  legacyError: (err: unknown) => void;
}

export type FeedCoordinatorMode = 'idle' | 'mailbox' | 'legacy';

export interface FeedCoordinator {
  start: (mailboxes: ReadonlyArray<string>, opts?: { legacy?: boolean }) => void;
  /** Re-point the listener set; only the added/removed mailboxes are touched. */
  setMailboxes: (mailboxes: ReadonlyArray<string>) => void;
  dispose: () => void;
  mode: () => FeedCoordinatorMode;
}

const noop: Unsubscribe = () => undefined;

/**
 * Owns the feed's listener lifecycle so the React effect only wires state setters.
 *
 *  - Nothing is published until EVERY initial listener delivered its first snapshot, so the
 *    badge and the new-mail sound see one complete picture instead of a half-loaded one.
 *  - ANY per-mailbox listener error (failed-precondition while the composite index builds,
 *    permission errors, ...) abandons the per-mailbox listeners and runs the legacy single
 *    query instead: a dead listener would otherwise leave that mailbox's mail silently
 *    missing from the feed.
 *  - After dispose() no callback ever reaches the hooks again (no state updates after unmount).
 */
export function createFeedCoordinator<T extends WindowEmail, C>(
  hooks: FeedCoordinatorHooks<T, C>
): FeedCoordinator {
  interface Entry {
    unsub: Unsubscribe;
    window: FeedWindow<T, C> | null;
  }
  const entries = new Map<string, Entry>();
  const pending = new Set<string>();
  let legacy: Entry | null = null;
  let mode: FeedCoordinatorMode = 'idle';
  let gateOpen = false;
  let loadedNotified = false;
  let disposed = false;

  const notifyLoaded = () => {
    if (loadedNotified) return;
    loadedNotified = true;
    hooks.initialLoaded();
  };

  const liveWindows = (): FeedWindow<T, C>[] => {
    const out: FeedWindow<T, C>[] = [];
    entries.forEach((e) => {
      if (e.window) out.push(e.window);
    });
    return out;
  };

  const openGateIfReady = () => {
    // After a fallback the per-mailbox gate is moot: the legacy listener owns the initial load.
    if (mode !== 'mailbox' || gateOpen || pending.size > 0) return;
    gateOpen = true;
    hooks.publish(liveWindows(), true);
    notifyLoaded();
  };

  const startLegacy = () => {
    const entry: Entry = { unsub: noop, window: null };
    legacy = entry;
    const unsub = hooks.subscribe(null, LEGACY_FEED_LIMIT, {
      onSnapshot: (raw) => {
        if (disposed || legacy !== entry) return;
        const first = entry.window === null;
        const w = legacyWindow(raw, hooks.cutoffMs, hooks.cursorOf);
        entry.window = w;
        hooks.publish([w], first);
        notifyLoaded();
      },
      onError: (err) => {
        if (disposed || legacy !== entry) return;
        hooks.legacyError(err);
        notifyLoaded();
      },
    });
    // A synchronous callback may already have torn us down; do not leak the listener.
    if (disposed || legacy !== entry) unsub();
    else entry.unsub = unsub;
  };

  const fallbackToLegacy = (reason: unknown) => {
    if (disposed || mode === 'legacy') return;
    mode = 'legacy';
    entries.forEach((e) => e.unsub());
    entries.clear();
    pending.clear();
    hooks.fallback(reason);
    startLegacy();
  };

  const subscribeMailbox = (key: string) => {
    // A synchronous failure of an earlier sibling may already have switched us to legacy mid-loop.
    if (disposed || mode !== 'mailbox') return;
    const limit = feedLimitForMailbox(key);
    const entry: Entry = { unsub: noop, window: null };
    entries.set(key, entry);
    // A mailbox added before the initial gate opened is part of the initial load.
    if (!gateOpen) pending.add(key);
    const unsub = hooks.subscribe(key, limit, {
      onSnapshot: (raw) => {
        if (disposed || mode !== 'mailbox' || entries.get(key) !== entry) return;
        const first = entry.window === null;
        entry.window = mailboxWindow(key, raw, limit, hooks.cutoffMs, hooks.cursorOf);
        pending.delete(key);
        if (!gateOpen) {
          openGateIfReady();
          return;
        }
        hooks.publish(liveWindows(), first);
      },
      onError: (err) => {
        if (disposed || mode !== 'mailbox' || entries.get(key) !== entry) return;
        fallbackToLegacy(err);
      },
    });
    if (disposed || entries.get(key) !== entry) unsub();
    else entry.unsub = unsub;
  };

  return {
    start(mailboxes, opts) {
      if (disposed || mode !== 'idle') return;
      if (opts && opts.legacy) {
        mode = 'legacy';
        startLegacy();
        return;
      }
      mode = 'mailbox';
      normalizeMailboxList(mailboxes).forEach(subscribeMailbox);
      openGateIfReady();
    },

    setMailboxes(mailboxes) {
      if (disposed || mode !== 'mailbox') return;
      const currentKeys: string[] = [];
      entries.forEach((_e, k) => currentKeys.push(k));
      const { added, removed } = diffMailboxes(currentKeys, normalizeMailboxList(mailboxes));
      if (added.length === 0 && removed.length === 0) return;
      removed.forEach((key) => {
        const e = entries.get(key);
        entries.delete(key);
        pending.delete(key);
        if (e) e.unsub();
      });
      added.forEach(subscribeMailbox);
      if (mode !== 'mailbox') return; // an added listener failed synchronously and we fell back
      if (!gateOpen) openGateIfReady();
      else if (removed.length > 0) hooks.publish(liveWindows(), false);
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      entries.forEach((e) => e.unsub());
      entries.clear();
      pending.clear();
      if (legacy) {
        legacy.unsub();
        legacy = null;
      }
    },

    mode: () => mode,
  };
}

// -- drafts ------------------------------------------------------------------------------------

/** Firestore Timestamp | Date | ISO string | epoch millis | {seconds,nanoseconds} -> epoch ms (0 if unusable). */
export function timeValueToMs(value: unknown): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : 0;
  }
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : 0;
  }
  if (typeof value === 'object') {
    const o = value as any;
    try {
      if (typeof o.toMillis === 'function') {
        const t = o.toMillis();
        return typeof t === 'number' && Number.isFinite(t) ? t : 0;
      }
      if (typeof o.toDate === 'function') {
        const d = o.toDate();
        const t = d instanceof Date ? d.getTime() : NaN;
        return Number.isFinite(t) ? t : 0;
      }
    } catch {
      return 0;
    }
    const secs = typeof o.seconds === 'number' ? o.seconds : typeof o._seconds === 'number' ? o._seconds : null;
    if (secs !== null) {
      const nanos = typeof o.nanoseconds === 'number' ? o.nanoseconds : typeof o._nanoseconds === 'number' ? o._nanoseconds : 0;
      return secs * 1000 + Math.floor(nanos / 1e6);
    }
  }
  return 0;
}

/**
 * When a draft was last touched: updatedAt, else syncedAt, else createdAt. Outlook-synced drafts
 * created before updatedAt was stamped carry only syncedAt/createdAt, which is why ordering by
 * updatedAt in the query silently excluded them.
 */
export function draftRecencyMs(doc: { updatedAt?: unknown; syncedAt?: unknown; createdAt?: unknown }): number {
  return timeValueToMs(doc.updatedAt) || timeValueToMs(doc.syncedAt) || timeValueToMs(doc.createdAt);
}

/** Stable newest-first sort by an arbitrary millisecond key. */
export function sortNewestFirstBy<T>(items: ReadonlyArray<T>, msOfItem: (item: T) => number): T[] {
  return items
    .map((item, index) => ({ item, index, ms: msOfItem(item) }))
    .sort((a, b) => b.ms - a.ms || a.index - b.index)
    .map((x) => x.item);
}
